import { log as debugLog, error as debugError } from '@/utils/debugLogger';

export const DOWNLOAD_MANAGER = {
  namespaced: true,
  state: {
    isVisible: false,
    isMinimized: false,
    downloads: [],
    downloadControllers: new Map()
  },
  getters: {
    GET_IS_VISIBLE: state => state.isVisible,
    GET_IS_MINIMIZED: state => state.isMinimized,
    GET_DOWNLOADS: state => state.downloads,
    GET_ACTIVE_DOWNLOADS: state => state.downloads.filter(d => d.status === 'downloading' || d.status === 'paused'),
    GET_COMPLETED_DOWNLOADS: state => state.downloads.filter(d => d.status === 'completed' || d.status === 'error' || d.status === 'cancelled'),
    HAS_COMPLETED_DOWNLOADS: state => state.downloads.some(d => d.status === 'completed' || d.status === 'error' || d.status === 'cancelled'),
    GET_DOWNLOAD_BY_ID: state => id => state.downloads.find(d => d.id === id),
    GET_CONTROLLER_BY_ID: state => id => state.downloadControllers.get(id)
  },
  mutations: {
    SET_VISIBILITY(state, isVisible) {
      state.isVisible = isVisible;
    },
    SET_MINIMIZED(state, isMinimized) {
      state.isMinimized = isMinimized;
    },
    ADD_DOWNLOAD(state, download) {
      state.downloads.unshift(download);
      if (!state.isVisible) {
        state.isVisible = true;
        state.isMinimized = false;
      }
    },
    UPDATE_DOWNLOAD(state, { id, updates }) {
      const download = state.downloads.find(d => d.id === id);
      if (download) {
        Object.assign(download, updates);
      }
    },
    REMOVE_DOWNLOAD(state, id) {
      const index = state.downloads.findIndex(d => d.id === id);
      if (index > -1) {
        state.downloads.splice(index, 1);
        // Remove controller if exists
        if (state.downloadControllers.has(id)) {
          state.downloadControllers.delete(id);
        }
      }

      // Hide if no downloads left
      if (state.downloads.length === 0) {
        state.isVisible = false;
      }
    },
    SET_DOWNLOAD_CONTROLLER(state, { id, controller }) {
      state.downloadControllers.set(id, controller);
    },
    REMOVE_DOWNLOAD_CONTROLLER(state, downloadId) {
      if (state.downloadControllers.has(downloadId)) {
        const controller = state.downloadControllers.get(downloadId);
        if (controller && !controller.signal.aborted) {
          controller.abort();
        }
        state.downloadControllers.delete(downloadId);
      }
    },
    CLEAR_COMPLETED(state) {
      state.downloads = state.downloads.filter(d => d.status === 'downloading' || d.status === 'paused');

      if (state.downloads.length === 0) {
        state.isVisible = false;
      }
    },
    CLEAR_ALL(state) {
      // Abort all active controllers
      for (const [, controller] of state.downloadControllers.entries()) {
        if (!controller.signal.aborted) {
          controller.abort();
        }
      }

      state.downloads = [];
      state.downloadControllers.clear();
      state.isVisible = false;
    }
  },
  actions: {
    SHOW({ commit }) {
      commit('SET_VISIBILITY', true);
      commit('SET_MINIMIZED', false);
    },
    HIDE({ commit }) {
      commit('SET_VISIBILITY', false);
    },
    TOGGLE_MINIMIZE({ commit, state }) {
      commit('SET_MINIMIZED', !state.isMinimized);
    },
    ADD_DOWNLOAD({ commit, dispatch }, { url, filename = null }) {
      const downloadId = Date.now() + Math.random();
      const extractedFilename = filename || dispatch('EXTRACT_FILENAME_FROM_URL', url);

      const download = {
        id: downloadId,
        url: url,
        filename: extractedFilename,
        status: 'downloading',
        progress: 0,
        startTime: new Date(),
        isPaused: false,
        pausedAt: null,
        resumedAt: null,
        downloadedSize: 0,
        totalSize: 0
      };

      // Create AbortController for this download
      const controller = new AbortController();

      commit('ADD_DOWNLOAD', download);
      commit('SET_DOWNLOAD_CONTROLLER', { id: downloadId, controller });

      // Start the download through the service
      dispatch('START_DOWNLOAD', download);

      return downloadId;
    },
    UPDATE_DOWNLOAD_PROGRESS({ commit, state }, { id, progress, downloadedSize, totalSize }) {
      const download = state.downloads.find(d => d.id === id);
      if (!download) return;

      const now = new Date();
      const elapsedTime = download.startTime ? (now - download.startTime) / 1000 : 0;
      const downloadSpeed = elapsedTime > 0 ? downloadedSize / elapsedTime : 0;
      const estimatedTimeRemaining = downloadSpeed > 0 && totalSize > downloadedSize ?
        (totalSize - downloadedSize) / downloadSpeed : null;

      const updates = {
        progress,
        downloadedSize,
        totalSize,
        lastUpdateTime: now,
        downloadSpeed,
        estimatedTimeRemaining
      };

      commit('UPDATE_DOWNLOAD', { id, updates });
    },
    UPDATE_DOWNLOAD_STATUS({ commit }, { id, status, progress }) {
      const updates = { status };
      if (progress !== undefined) {
        updates.progress = progress;
      }
      if (status === 'completed') {
        updates.completedTime = new Date();
      }

      commit('UPDATE_DOWNLOAD', { id, updates });
    },
    PAUSE_DOWNLOAD({ commit, state }, id) {
      const download = state.downloads.find(d => d.id === id);
      if (download && download.status === 'downloading') {
        commit('UPDATE_DOWNLOAD', {
          id,
          updates: {
            isPaused: true,
            pausedAt: new Date(),
            status: 'paused'
          }
        });
      }
    },
    RESUME_DOWNLOAD({ commit, dispatch, state }, id) {
      const download = state.downloads.find(d => d.id === id);
      if (download && download.status === 'paused') {
        commit('UPDATE_DOWNLOAD', {
          id,
          updates: {
            isPaused: false,
            resumedAt: new Date(),
            status: 'downloading'
          }
        });

        // Create new AbortController for resume
        const controller = new AbortController();
        commit('SET_DOWNLOAD_CONTROLLER', { id, controller });

        // Resume download from current position through service
        // Make sure to include the stored fileHandle for seamless resume
        const updatedDownload = {
          ...download,
          isPaused: false,
          status: 'downloading',
          resumedAt: new Date(),
          fileHandle: download.fileHandle // Preserve the file handle for resume
        };
        dispatch('START_DOWNLOAD', updatedDownload);
      }
    },
    CANCEL_DOWNLOAD({ commit, state }, id) {
      commit('REMOVE_DOWNLOAD_CONTROLLER', id);

      const download = state.downloads.find(d => d.id === id);
      if (download) {
        commit('UPDATE_DOWNLOAD', {
          id,
          updates: {
            status: 'cancelled',
            cancelledAt: new Date()
          }
        });
      }
    },
    RETRY_DOWNLOAD({ commit, dispatch, state }, id) {
      const download = state.downloads.find(d => d.id === id);
      if (download) {
        // Reset download state
        const updates = {
          status: 'downloading',
          progress: 0,
          downloadedSize: 0,
          errorMessage: null,
          retryCount: (download.retryCount || 0) + 1,
          isPaused: false,
          fileHandle: null // Remove stored fileHandle for fresh retry
        };

        commit('UPDATE_DOWNLOAD', { id, updates });

        // Create new AbortController for retry
        const controller = new AbortController();
        commit('SET_DOWNLOAD_CONTROLLER', { id, controller });

        // Start download again through service
        const updatedDownload = { ...download, ...updates };
        dispatch('START_DOWNLOAD', updatedDownload);
      }
    },
    REMOVE_DOWNLOAD({ commit }, id) {
      commit('REMOVE_DOWNLOAD_CONTROLLER', id);
      commit('REMOVE_DOWNLOAD', id);
    },
    CLEAR_COMPLETED({ commit }) {
      commit('CLEAR_COMPLETED');
    },
    CLEAR_ALL({ commit }) {
      commit('CLEAR_ALL');
    },
    EXTRACT_FILENAME_FROM_URL(_, url) {
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const filename = pathname.split('/').pop();
        return filename || 'download';
      } catch (error) {
        return 'download';
      }
    },
    // This will be implemented by the service since it requires file system access
    START_DOWNLOAD({ commit }, download) {
      // Import the service dynamically to avoid circular dependencies
      return import('@/services/downloadManagerService').then(module => {
        const downloadManagerService = module.default;
        return downloadManagerService.startDownload(download);
      }).catch(error => {
        debugError('Failed to start download via service:', error);
        commit('UPDATE_DOWNLOAD', {
          id: download.id,
          updates: {
            status: 'error',
            errorMessage: error.message || 'Failed to start download'
          }
        });
      });
    }
  }
};